import type { AccountType, CurrentUser, Role, RoleCapabilities } from './types'
import { hasRole, isBuilder } from './types'

type Actor = Pick<CurrentUser, 'role' | 'roles'> & { isAccountAdmin?: boolean; account: { accountType: AccountType } }

/**
 * What a user may do, from the account type, the role and the admin flag. Designers and editors build; reviewers and
 * approvers only see the projects they are assigned to. Only an account admin manages users, and only a SupplyLogic
 * admin manages other accounts.
 */
export function capabilitiesOfUser(user: Actor): RoleCapabilities {
  const type = user.account.accountType
  const builder = isBuilder(user)
  const admin = Boolean(user.isAccountAdmin)
  return {
    projects: !builder ? 'ASSIGNED' : type === 'SUPPLY_LOGIC' ? 'ALL' : type === 'SUPPLIER' ? 'LINKED_CLIENTS' : 'OWN_CLIENT',
    users: admin && type === 'SUPPLY_LOGIC' ? 'ALL' : 'OWN_ACCOUNT',
    createAccountTypes: admin && type === 'SUPPLY_LOGIC' ? ['SUPPLIER', 'CLIENT'] : [],
    generate: builder,
    approve: hasRole(user, 'APPROVER'),
  }
}

export const capabilitiesOf = (accountType: AccountType, role: Role, isAccountAdmin = false) => capabilitiesOfUser({ role, isAccountAdmin, account: { accountType } })

export const isSupplyLogicAdmin = (user: Actor) => user.account.accountType === 'SUPPLY_LOGIC' && Boolean(user.isAccountAdmin)

/** Who sees the Users screen and may add people. Scope (all accounts vs own) comes from `usersScopeOf`. */
export const usersScopeOf = (user: Actor): RoleCapabilities['users'] => capabilitiesOfUser(user).users

/** Everyone can see the Users screen (their own account's people); only account admins create, edit and remove people. */
export const canManageUsers = (user: Actor) => Boolean(user.isAccountAdmin)

/** Only SupplyLogic admins create supplier and client accounts. Supplier admins never onboard clients. */
export const canCreateAccountType = (user: Actor, type: AccountType) =>
  (capabilitiesOfUser(user).createAccountTypes ?? []).includes(type)
